class Map {
    constructor(container, options = {}) {
        if (!(container instanceof Element)) {
            throw Error('A container element must be specified.');
        }

        this._container = container;
        this._options = options;

        this._dragging = 'dragging' in options ? options.dragging : true;
        this._touchZoom = 'touchZoom' in options ? options.touchZoom : true;
        this._scrollWheelZoom =
            'scrollWheelZoom' in options ? options.scrollWheelZoom : true;
        this._doubleClickZoom =
            'doubleClickZoom' in options ? options.doubleClickZoom : true;
        this._tap = 'tap' in options ? options.tap : true;
        this._closePopupOnClick =
            'closePopupOnClick' in options ? options.closePopupOnClick : true;
        this._keyboard = 'keyboard' in options ? options.keyboard : true;
        this._zoomControl =
            'zoomControl' in options ? options.zoomControl : true;

        this._loadCallback = null;
        if (options.onLoad) {
            this._loadCallback = options.onLoad.bind(this);
        }

        this._clickCallback = null;
        if (options.onClick) {
            this._clickCallback = options.onClick.bind(this);
        }

        this._scriptPromise = new Promise(resolve => {
            const scriptEle = document.createElement('script');
            scriptEle.src =
                'https://maptiles.finncdn.no/rel/head/js/external/leaflet/v0.7.7/leaflet.js';
            document.body.appendChild(scriptEle);
            scriptEle.onload = resolve;
        });

        this._stylePromise = new Promise(resolve => {
            const styleEle = document.createElement('link');
            styleEle.href =
                'https://maptiles.finncdn.no/rel/head/js/external/leaflet/v0.7.7/leaflet.css';
            styleEle.rel = 'stylesheet';
            document.head.appendChild(styleEle);
            styleEle.onload = resolve;
        });
    }

    init() {
        this._initializing = Promise.all([
            this._stylePromise,
            this._scriptPromise,
        ]).then(() => this._init());
    }

    _init() {
        this._map = this._createMap();
        this._mapMarker = this._createMapMarker();
        this._mapRadius = this._createMapRadius();
        this._mapPopup = this._createMapPopup();

        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this._map.invalidateSize();
            }, 400);
        });

        if (this._loadCallback) {
            this._loadCallback();
        }
    }

    setDefaultCenterAndZoom() {
        if (this._map.hasLayer(this._mapMarker)) {
            this._map.removeLayer(this._mapMarker);
        }
        if (this._map.hasLayer(this._mapRadius)) {
            this._map.removeLayer(this._mapRadius);
        }

        this._map.setView(Map.DEFAULT_CENTER, Map.DEFAULT_ZOOM);
    }

    clearMarkerAndRadius() {
        this._maybeRunAsync(() => this._clearMarkerAndRadius());
    }

    _clearMarkerAndRadius() {
        if (this._map.hasLayer(this._mapMarker)) {
            this._map.removeLayer(this._mapMarker);
        }
        if (this._map.hasLayer(this._mapRadius)) {
            this._map.removeLayer(this._mapRadius);
        }
    }

    setCenterAndPan(latlng) {
        this._mapMarker.setLatLng(latlng);
        this._mapRadius.setLatLng(latlng);
        this._mapPopup.setLatLng(latlng);

        const panOptions = {
            animate: true,
            pan: { duration: 0.5 },
        };

        this._map.setView(
            latlng,
            this._map.getBoundsZoom(this._mapRadius.getBounds()),
            panOptions
        );
    }

    setMarkerAndPan(latlng) {
        this._maybeRunAsync(() => this._setMarkerAndPan(latlng));
    }

    _setMarkerAndPan(latlng) {
        this._mapMarker.setLatLng(latlng);
        this._mapRadius.setLatLng(latlng);
        this._mapPopup.setLatLng(latlng);

        if (!this._map.hasLayer(this._mapMarker)) {
            this._mapMarker.addTo(this._map);
        }

        const panOptions = {
            animate: true,
            pan: { duration: 0.5 },
        };

        this._map.setView(
            latlng,
            this._map.getBoundsZoom(this._mapRadius.getBounds()),
            panOptions
        );
    }

    _maybeRunAsync(func) {
        if (this._initializing) {
            this._initializing.then(() => func());
        }
    }

    setRadiusAndZoom(radius) {
        this._maybeRunAsync(() => this._setRadiusAndZoom(radius));
    }

    _setRadiusAndZoom(radius) {
        this._mapRadius.setRadius(radius);

        if (!this._map.hasLayer(this._mapRadius)) {
            this._mapRadius.addTo(this._map);
        }

        const zoomOptions = {
            animate: true,
        };

        this._map.setView(
            this._mapRadius.getLatLng(),
            this._map.getBoundsZoom(this._mapRadius.getBounds()),
            zoomOptions
        );
    }

    setMarkerAndRadius(latlng, radius) {
        this._maybeRunAsync(() => this._setMarkerAndRadius(latlng, radius));
    }

    _setMarkerAndRadius(latlng, radius) {
        this._mapMarker.setLatLng(latlng);
        this._mapRadius.setLatLng(latlng);
        this._mapPopup.setLatLng(latlng);

        this._mapRadius.setRadius(radius);

        if (!this._map.hasLayer(this._mapMarker)) {
            this._mapMarker.addTo(this._map);
        }
        if (!this._map.hasLayer(this._mapRadius)) {
            this._mapRadius.addTo(this._map);
        }

        const zoomPanOptions = {
            animate: true,
            pan: { duration: 0.5 },
        };

        this._map.setView(
            latlng,
            this._map.getBoundsZoom(this._mapRadius.getBounds()),
            zoomPanOptions
        );
    }

    setPopupContentAndOpen(content) {
        this._mapMarker
            .bindPopup(this._mapPopup, { offset: [14, -28] })
            .setPopupContent(content)
            .openPopup();
    }

    _createMap() {
        const mapOptions = {
            center: Map.DEFAULT_CENTER,
            zoom: Map.DEFAULT_ZOOM,
            // minZoom: Map.DEFAULT_ZOOM,
            dragging: this._dragging,
            touchZoom: this._touchZoom,
            scrollWheelZoom: this._scrollWheelZoom,
            doubleClickZoom: this._doubleClickZoom,
            tap: this._tap,
            closePopupOnClick: this._closePopupOnClick,
            keyboard: this._keyboard,
            zoomControl: this._zoomControl,
        };

        const map = L.map(this._container, mapOptions);
        L.tileLayer(
            'https://maptiles.finncdn.no/tileService/1.0.3/normap/{z}/{x}/{y}.png',
            {
                attribution: '&copy; Norkart ',
            }
        ).addTo(map);

        // map.setMaxBounds(map.getBounds());

        if (this._clickCallback) {
            map.on('click', this._clickCallback);
        }

        return map;
    }

    _createMapMarker() {
        const markerIcon = L.icon({
            iconUrl: 'https://maptiles.finncdn.no/rel/head/images/pin_r.png',
            iconSize: [28, 28],
            iconAnchor: [0, 28],
        });

        const markerOptions = {
            icon: markerIcon,
            clickable: false,
            keyboard: false,
        };

        return L.marker(Map.DEFAULT_CENTER, markerOptions);
    }

    _createMapRadius() {
        const radiusOptions = {
            color: '#0063FB',
            opacity: 0,
            fillOpacity: 0.35,
        };

        return L.circle(Map.DEFAULT_CENTER, Map.DEFAULT_RADIUS, radiusOptions);
    }

    _createMapPopup() {
        const popupOptions = {
            closeButton: false,
            className: 'b1 licorice roundify',
        };

        return L.popup(popupOptions);
    }
}

Map.DEFAULT_CENTER = [66.314588, 14.141348];
Map.DEFAULT_RADIUS = 10000;
Map.DEFAULT_ZOOM = 3;

export default Map;



// WEBPACK FOOTER //
// ./lib/Map.js